import { SessionIdUtil, DeviceIdUtil } from "@amerisave/amc-client-beacon";
import { getTrackingInfo } from "@/components/amc-pixel/amc-pixel-utils";
import {
  GtmEvent,
  GtmEventName,
  GtmTrackData,
  GtmTrackOptions,
  GtmBaseEventData,
} from "./gtm-events-types";
import type { SnakeCasedProperties } from "type-fest";
import { insertGASession } from "@/services/backend-service";
import { getCookie } from "@/utilities/cookie";

class GtmEventsService {
  private previousUrl: string = "";
  isInitialized: boolean = false;

  initialize(): void {
    if (typeof window === "undefined" || this.isInitialized) {
      return;
    }

    this.insertSession();
    this.previousUrl = window.location.href;
    this.isInitialized = true;
  }

  getEvent = (eventName: GtmEventName): GtmEvent => {
    return window.dataLayer?.find((event) => event.event === eventName);
  };

  insertSession = async (): Promise<void> => {
    const trackingInfo = await getTrackingInfo();
    const sessionId = new SessionIdUtil().get();
    const gaClientId = getCookie("_ga")?.toString();
    const gaSessionId = getCookie(
      `_ga_${process.env.NEXT_PUBLIC_GA_CONTAINER_ID}`,
    )?.toString();
    const { clickId, sourceInfoId, visitorId } = trackingInfo;
    const fbp = getCookie("_fbp")?.toString();
    const fbc = getCookie("_fbc")?.toString();

    if (!gaClientId || !gaSessionId) {
      return;
    }

    insertGASession({
      clickId,
      gaClientId,
      gaSessionId,
      source: "amerisave-nova",
      sourceInfoId,
      sourceSessionId: sessionId,
      visitorId,
      fbp,
      fbc,
    });
  };

  async track<T extends GtmEventName>(
    eventName: T,
    data: GtmTrackData<T>,
    options: GtmTrackOptions = {},
  ): Promise<void> {
    if (typeof window === "undefined") {
      return;
    }

    try {
      const { overrides } = options;
      const baseData = await this.getBaseEventData(overrides);

      const mergedData = { ...baseData, ...data };

      const snakeCaseData = this.convertObjectToSnakeCase(mergedData);

      const eventData = {
        event: eventName,
        ...snakeCaseData,
      } as SnakeCasedProperties<GtmEvent>;

      this.pushToDataLayer(eventData as any);

      if (eventName === "page_view") {
        this.previousUrl = window.location.href;
      }
    } catch (error) {
      console.error("Error tracking GTM event:", error);
    }
  }

  trackCustom(eventName: string, data: Record<string, any>): void {
    try {
      if (typeof window === "undefined") {
        return;
      }

      const eventData = {
        event: eventName,
        ...data,
      };

      this.pushToDataLayer(eventData as any);
    } catch (error) {
      console.error("Error tracking GTM event:", error);
    }
  }

  updatePreviousUrl(url: string): void {
    this.previousUrl = url;
  }

  async getBaseEventData(
    overrides?: Partial<GtmBaseEventData>,
  ): Promise<Omit<GtmBaseEventData, "event">> {
    if (typeof window === "undefined") {
      throw new Error(
        "Gtm Events Service can only be used in browser environment.",
      );
    }

    const trackingInfo = await getTrackingInfo();
    const sessionIdUtil = new SessionIdUtil();
    const deviceIdUtil = new DeviceIdUtil();
    const urlParams = new URLSearchParams(window.location.search);
    const utmCampaign = urlParams.get("utm_campaign");
    const utmMedium = urlParams.get("utm_medium");
    const utmSource = urlParams.get("utm_source");
    const now = new Date();
    const pageName = this.getPageName(window.location.pathname);

    const baseData = this.sanitizeEventData({
      amerisaveAdvertiserBuyId: trackingInfo.advertiserBuyId,
      amerisaveAdvertiserId: trackingInfo.advertiserId,
      amerisaveClickId: trackingInfo.clickId,
      amerisaveClientId: deviceIdUtil.get(),
      amerisaveCustomerId: trackingInfo.customerId,
      amerisaveSessionId: sessionIdUtil.get(),
      amerisaveSourceId: trackingInfo.sourceId,
      amerisaveSourceInfoId: trackingInfo.sourceInfoId,
      external_id: trackingInfo.sourceInfoId,
      amerisaveSourceTransType: trackingInfo.sourceTransactionType,
      amerisaveUserId: trackingInfo.visitorId,
      amerisaveVisitorId: trackingInfo.visitorId,
      environment: this.getEnvironment(),
      externalReferrer: document.referrer,
      funnelCampaign: utmCampaign || undefined,
      funnelMedium: utmMedium || undefined,
      funnelSource: utmSource || undefined,
      offeringTcType: undefined,
      pageLocation: window.location.href,
      // TODO: Test page name
      pageName: pageName,
      pageReferrer: document.referrer,
      pageTitle: document.title,
      // TODO: Test previous url
      previousUrl: this.previousUrl,
      timestamp: now.toISOString(),
      timezoneOffset: now.getTimezoneOffset(),
    });

    if (overrides) {
      return { ...baseData, ...overrides };
    }

    return baseData;
  }

  private sanitizeEventData<D extends Record<string, any>>(eventData: D): D {
    const processedEventData = {} as D;
    for (const key in eventData) {
      (processedEventData as any)[key] = this.sanitizeValue(
        (eventData as any)[key],
      );
    }
    return processedEventData;
  }

  private pushToDataLayer(eventData: GtmEvent): void {
    if (typeof window === "undefined") {
      return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(eventData);
  }

  private getEnvironment(): "development" | "production" {
    return process.env.NODE_ENV === "development" || !process.env.NODE_ENV
      ? "development"
      : "production";
  }

  private getPageName(pathname: string): string {
    if (pathname === "/") {
      return "home";
    }

    const cleanPath = pathname.replace(/^\//, "").replace(/\//g, "-");

    if (cleanPath.includes("[...slug]")) {
      return cleanPath.replace("[...slug]", "dynamic");
    }

    return cleanPath || "unknown";
  }

  private convertObjectToSnakeCase(
    data: Record<string, any>,
  ): Record<string, any> {
    const converted: Record<string, any> = {};

    for (const key in data) {
      if (key.match(/^[a-z][a-zA-Z0-9]*$/)) {
        const snakeKey = key.replace(/([A-Z])/g, "_$1").toLowerCase();
        converted[snakeKey] = data[key];
      } else {
        converted[key] = data[key];
      }
    }

    return converted;
  }

  private sanitizeValue(
    value: string | number | undefined | null,
  ): string | undefined {
    if (typeof value === "number") {
      value = String(value);
    }
    if (value === "0" || !value) {
      return undefined;
    }
    return value;
  }
}

export const gtmEventsService = new GtmEventsService();

declare global {
  interface Window {
    dataLayer: any[];
  }
}
